import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import posthog from '../posthog';
import { getUserUsage } from '../api'; // Assuming api.js is in ../
import { 
    signOut as authSignOut, 
    isSignedIn, 
    startImpersonation as authStartImpersonation, 
    stopImpersonation as authStopImpersonation, 
    isImpersonating as authIsImpersonating, 
    getImpersonationTargetId 
} from '../auth'; // Assuming auth.js is in ../

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // Add state to track impersonation status to trigger re-renders
    const [impersonating, setImpersonating] = useState(authIsImpersonating());

    const identifyUser = useCallback((u) => {
        if (!u) return;
        posthog.identify(String(u.id), {
            email: u.username,
            is_admin: !!u.is_admin,
            compress_credits: u.compress_credits,
            impersonating: authIsImpersonating(),
            impersonation_target_id: getImpersonationTargetId() || undefined
        });
    }, []);

    const fetchUser = useCallback(async () => {
        if (!isSignedIn()) {
            setLoading(false);
            setUser(null);
            return;
        }
        setLoading(true);
        try {
            const userData = await getUserUsage();
            setUser(userData);
            identifyUser(userData);
        } catch (error) {
            console.error("Failed to fetch user data:", error);
            authSignOut(); 
            setUser(null);
            posthog.reset();
        } finally {
            setLoading(false);
        }
    }, [identifyUser]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    // Functions to wrap auth functions and update local state
    const startImpersonation = useCallback((targetUserId) => {
        authStartImpersonation(targetUserId);
        setImpersonating(true);
        posthog.capture('impersonation_start', { target_user_id: targetUserId });
        fetchUser();
    }, [fetchUser]);

    const stopImpersonation = useCallback(() => {
        const targetId = getImpersonationTargetId();
        authStopImpersonation();
        setImpersonating(false);
        if (targetId) {
            posthog.capture('impersonation_stop', { target_user_id: targetId });
        }
        fetchUser();
    }, [fetchUser]);

    const signOut = useCallback(() => {
        stopImpersonation(); // Ensure impersonation stops on sign out
        authSignOut();
        setUser(null); 
        posthog.reset(); // returns to anonymous user
    }, [stopImpersonation]);

    const value = {
        user,
        isAdmin: user?.is_admin ?? false,
        isAuthenticated: !!user,
        loading,
        signOut,
        startImpersonation,
        stopImpersonation,
        isImpersonating: impersonating, // Use state for reactivity
        getImpersonationTargetId,
        refetchUser: fetchUser // Expose refetch function if needed
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === null) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
